import type { StylesConfig, Theme } from 'react-select'
import { colors, font } from '../tokens'

/**
 * Token-based styling for `react-select`. Pass both to the component:
 *
 *   <Select styles={reactSelectStyles} theme={reactSelectTheme} ... />
 *
 * or use the `ReactSelect` wrapper from `ui`, which does this for you.
 */
export const reactSelectStyles: StylesConfig<any, any, any> = {
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        fontFamily: font,
        fontSize: 13,
        borderRadius: 6,
        backgroundColor: state.isDisabled ? colors.surface2 : colors.surface,
        borderColor: state.isFocused ? colors.accent : colors.line,
        boxShadow: state.isFocused ? `0 0 0 3px rgba(91, 112, 137, 0.14)` : 'none',
        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': { borderColor: state.isFocused ? colors.accent : colors.lineStrong },
    }),
    valueContainer: (base) => ({ ...base, padding: '2px 10px' }),
    placeholder: (base) => ({ ...base, color: colors.ink4 }),
    singleValue: (base) => ({ ...base, color: colors.ink }),
    input: (base) => ({ ...base, color: colors.ink, fontFamily: font }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
        ...base,
        padding: '0 10px',
        color: state.isFocused ? colors.accent : colors.ink4,
        '&:hover': { color: colors.accent },
    }),
    clearIndicator: (base) => ({ ...base, padding: '0 6px', color: colors.ink4, '&:hover': { color: colors.bad } }),
    menu: (base) => ({
        ...base,
        zIndex: 30,
        borderRadius: 6,
        border: `1px solid ${colors.line}`,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
        overflow: 'hidden',
        fontFamily: font,
        fontSize: 13,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 1200 }),
    option: (base, state) => ({
        ...base,
        fontSize: 13,
        padding: '9px 12px',
        color: state.isSelected ? colors.accent : colors.ink,
        fontWeight: state.isSelected ? 600 : 400,
        backgroundColor: state.isSelected ? colors.accentTint : state.isFocused ? colors.surface2 : colors.surface,
        cursor: 'pointer',
        ':active': { backgroundColor: colors.accentTint },
    }),
    multiValue: (base) => ({ ...base, backgroundColor: colors.accentTint, borderRadius: 4 }),
    multiValueLabel: (base) => ({ ...base, color: colors.ink2, fontSize: 12, fontWeight: 500 }),
    multiValueRemove: (base) => ({
        ...base,
        color: colors.ink3,
        ':hover': { backgroundColor: colors.badTint, color: colors.bad },
    }),
    noOptionsMessage: (base) => ({ ...base, color: colors.ink3, fontSize: 13 }),
    loadingMessage: (base) => ({ ...base, color: colors.ink3, fontSize: 13 }),
}

export const reactSelectTheme = (theme: Theme): Theme => ({
    ...theme,
    borderRadius: 6,
    colors: {
        ...theme.colors,
        primary: colors.accent,
        primary75: colors.accentSoft,
        primary50: colors.accentTintStrong,
        primary25: colors.accentTint,
        danger: colors.bad,
        dangerLight: colors.badTint,
        neutral0: colors.surface,
        neutral5: colors.surface2,
        neutral10: colors.surface3,
        neutral20: colors.line,
        neutral30: colors.lineStrong,
        neutral40: colors.ink4,
        neutral50: colors.ink4,
        neutral60: colors.ink3,
        neutral70: colors.ink3,
        neutral80: colors.ink,
        neutral90: colors.ink,
    },
})
